import React from "react";
import { z } from "zod";
import { ChatReference } from "./ChatReference";
import { ChatUrlReference } from "./ChatUrlReference";
import type { ChatReferenceLibraryItem } from "@src/features/aiAssist/hooks/useChatReferences";
import { Message } from "@ai-sdk/ui-utils";
import { AnnotationSchema } from "../assistantChat/ThreadMessage";

type Annotation = z.infer<typeof AnnotationSchema>;

interface MessageAnnotationsProps {
  message: Message;
  messageIndex: number;
  referencesLoading: boolean;
  // Keyed by either library_item_id (pgvector citations) or the search
  // document id parsed from the legacy `<uuid>.txt` filename (OpenAI
  // vector-store citations). useChatReferences builds the map the same way.
  libraryItemsByLookupId: Map<string, ChatReferenceLibraryItem>;
  onClose?: () => void;
}

// Produces the same key useChatReferences uses for the map: pgvector
// citations key off library_item_id; legacy citations key off the search
// document id parsed from the `<uuid>.txt` filename.
const getCitationLookupId = (citation: Annotation): string => {
  if (citation.type !== "file_citation") return citation.metadata.url;
  if (citation.metadata?.library_item_id) {
    return citation.metadata.library_item_id;
  }
  if (citation.filename?.endsWith(".txt")) {
    return citation.filename.replace(/\.txt$/, "");
  }
  return "";
};

export const MessageAnnotations: React.FC<MessageAnnotationsProps> = ({
  message,
  messageIndex,
  referencesLoading,
  libraryItemsByLookupId,
  onClose,
}) => {
  const validatedAnnotations = message
    .annotations!.map((ann) => AnnotationSchema.safeParse(ann))
    .filter((parseResult) => {
      if (!parseResult.success) {
        console.warn(
          "Invalid annotation:",
          parseResult.error.issues,
          parseResult.error.format(),
        );
        return false;
      }
      return (
        parseResult.data.type === "file_citation" ||
        parseResult.data.type === "intercom_article"
      );
    })
    .map((parseResult) => (parseResult.success ? parseResult.data : null))
    .filter((ann): ann is Annotation => ann !== null);

  if (validatedAnnotations.length === 0) return null;

  // Group by unique identifier (lookupId for file citations, url for URL
  // citations) and count references — multiple embeddings from the same
  // library item collapse to one card.
  const uniqueCitationsMap = new Map<string, Annotation>();
  const citationCounts: Record<string, number> = {};
  validatedAnnotations.forEach((ann: Annotation) => {
    const key = getCitationLookupId(ann);
    if (!key) return;
    if (!uniqueCitationsMap.has(key)) {
      uniqueCitationsMap.set(key, ann);
    }
    citationCounts[key] = (citationCounts[key] || 0) + 1;
  });
  const uniqueCitations = Array.from(uniqueCitationsMap.values());

  // Create citation number mapping for consistent numbering
  const citationNumberMap = new Map<string, number>();
  uniqueCitations.forEach((citation, index) => {
    const key = getCitationLookupId(citation);
    if (key) citationNumberMap.set(key, index + 1);
  });

  return (
    <div className="mt-2 flex flex-col gap-2">
      {referencesLoading
        ? // Show loading state while fetching library items
          uniqueCitations.map((citation, idx) => {
            const key = getCitationLookupId(citation);
            return (
              <div
                key={key || idx}
                className="flex items-center rounded-lg border border-gray-6 bg-white p-3 shadow-sm"
              >
                <div className="size-6 animate-pulse rounded bg-gray-3" />
                <div className="ml-3 flex-1">
                  <div className="h-4 w-32 animate-pulse rounded bg-gray-3" />
                  <div className="mt-1 h-3 w-24 animate-pulse rounded bg-gray-3" />
                </div>
              </div>
            );
          })
        : uniqueCitations.map((citation: Annotation, idx: number) => {
            if (citation.type === "file_citation") {
              const lookupId = getCitationLookupId(citation);
              const count = citationCounts[lookupId] || 1;
              const citationNumber = citationNumberMap.get(lookupId);
              const libraryItem = libraryItemsByLookupId.get(lookupId);

              return (
                <div
                  key={`${message.id}-${messageIndex}-annotation-file-${
                    lookupId || String(citation.file_id) || idx
                  }`}
                  className="rounded-lg border border-border shadow-sm transition-shadow duration-200 hover:shadow-md"
                >
                  <ChatReference
                    annotation={citation}
                    libraryItem={libraryItem}
                    referenceCount={count}
                    citationNumber={citationNumber}
                    onClose={onClose}
                  />
                </div>
              );
            } else {
              // URL citation
              const count = citationCounts[citation.metadata.url] || 1;
              const citationNumber = citationNumberMap.get(
                citation.metadata.url,
              );
              return (
                <div
                  key={`${message.id}-${messageIndex}-annotation-url-${idx}`}
                  className="rounded-lg border border-border shadow-sm transition-shadow duration-200 hover:shadow-md"
                >
                  <ChatUrlReference
                    annotation={citation}
                    referenceCount={count}
                    citationNumber={citationNumber}
                    onClose={onClose}
                  />
                </div>
              );
            }
          })}
    </div>
  );
};
